import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { HierarchyListResponse } from 'api/actions/hierarchy-list/hierarchy-list-response';
import { useApi } from 'api/api-context';
import HierarchyActions from 'components/tables/data/hierarchy/HierarchyActions';
import DataTable from 'components/tables/DataTable';
import {
  HierarchyIdCell,
  HierarchyLevelsCell,
  HierarchyNameCell,
  HierarchyOrganizationsCell,
} from 'components/tables/data/hierarchy/HierarchyCells';
import { getResponsiveActionsColumnWidth, useResponsiveDesign } from 'hooks/use-responsive-design';
import { useCallback, useMemo } from 'react';
import { ADD_HIERARCHY_PAGE_PATH } from 'routes/paths';

type HierarchyRow = HierarchyListResponse[number] & {
  levelLabels: string;
  directOrganizationNames: string;
};

/** Keeps row lifecycle permissions coherent after an in-place hierarchy transition. */
function withLifecycleState(row: HierarchyRow, change: { status: boolean }): HierarchyRow {
  const next = { ...row, ...change };
  return {
    ...next,
    actionPermissions: {
      ...next.actionPermissions,
      canDeactivate: next.status && !next.isDefault && next.directOrganizationCount === 0,
      canActivate: !next.status,
    },
  };
}

/** Modular table for global hierarchy administration. */
export default function HierarchyTable() {
  const { getAction } = useApi();
  const { tableActionMode } = useResponsiveDesign();
  const action = useCallback(async () => {
    const rows = await getAction('HierarchyList')();
    return rows.map((row) => ({
      ...row,
      levelLabels: row.levels.map(({ label }) => label).join(' / '),
      directOrganizationNames: row.directOrganizations.map(({ organizationName }) => organizationName).join(', '),
    }));
  }, [getAction]);

  const columns = useMemo<ColDef<HierarchyRow>[]>(
    () => [
      {
        field: 'hierarchyId',
        headerName: 'ID',
        width: 76,
        minWidth: 76,
        maxWidth: 76,
        sortable: true,
        unSortIcon: true,
        resizable: false,
        cellRenderer: HierarchyIdCell,
      },
      {
        field: 'hierarchyName',
        headerName: 'Názov',
        minWidth: 240,
        sortable: true,
        unSortIcon: true,
        resizable: true,
        cellRenderer: HierarchyNameCell,
      },
      {
        field: 'levelLabels',
        headerName: 'Úrovne',
        minWidth: 260,
        resizable: true,
        cellRenderer: HierarchyLevelsCell,
      },
      {
        field: 'directOrganizationNames',
        headerName: 'Priamo priradené organizácie',
        minWidth: 280,
        resizable: true,
        cellRenderer: HierarchyOrganizationsCell,
      },
      {
        field: 'status',
        headerName: 'Zaradená',
        valueGetter: ({ data }) => (data ? Number(data.status) : undefined),
        hide: true,
        filter: true,
        resizable: true,
      },
      {
        field: '_actions' as any,
        headerName: '',
        pinned: 'right',
        width: getResponsiveActionsColumnWidth(194, tableActionMode),
        minWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
        maxWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
        cellRenderer: ({ data, api }: ICellRendererParams<HierarchyRow>) =>
          data ? (
            <HierarchyActions
              hierarchyId={data.hierarchyId}
              hierarchyName={data.hierarchyName}
              status={data.status}
              isDefault={data.isDefault}
              {...data.actionPermissions}
              onChanged={(change) => {
                const node = api.getRowNode(String(data.hierarchyId));
                node?.setData(withLifecycleState(data, change));
              }}
            />
          ) : null,
      },
    ],
    [tableActionMode]
  );

  return (
    <DataTable
      tableId="hierarchies"
      title="Hierarchie"
      addButtonText="Pridať hierarchiu"
      addButtonTarget={ADD_HIERARCHY_PAGE_PATH.original}
      toggleDiscardedLabel="Zobraziť vyradené"
      columns={columns}
      action={action}
      getRowId={({ data }: { data: HierarchyRow }) => String(data.hierarchyId)}
      dataExport={{
        modalTitle: 'Exportovať hierarchie',
        fileName: 'hierarchie.xlsx',
        columnKeys: ['hierarchyId', 'hierarchyName', 'levelLabels', 'directOrganizationNames', 'status'],
      }}
    />
  );
}
